/** Which card is showing, how far through it is, and the controls that move it. */

import { useCallback, useEffect, useRef, useState } from "react";

import type { CardSpec } from "./deck";

const TICK_MS = 100;

export function usePlayer(deck: CardSpec[]) {
  const [index, setIndex] = useState(0);
  const [paused, setPausedState] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  // Read by the ticker, so a hold stops the clock at once rather than a
  // render later; ticks already queued then add nothing.
  const pausedNow = useRef(false);
  const setPaused = useCallback((value: boolean | ((was: boolean) => boolean)) => {
    pausedNow.current = typeof value === "function" ? value(pausedNow.current) : value;
    setPausedState(pausedNow.current);
  }, []);
  const card = deck[index];
  const last = deck.length - 1;

  const goTo = useCallback(
    (to: number) => {
      setIndex(Math.max(0, Math.min(to, last)));
      setElapsed(0);
    },
    [last],
  );
  const next = useCallback(() => {
    setIndex((at) => Math.min(at + 1, last));
    setElapsed(0);
  }, [last]);
  const prev = useCallback(() => {
    setIndex((at) => Math.max(at - 1, 0));
    setElapsed(0);
  }, []);

  // A card's bar fills while it plays, then the card stays put: the story only
  // moves on when someone taps, swipes an arrow or presses a key.
  const timed = Boolean(card) && !card.waitsForTap;
  const length = card ? card.seconds * 1000 : 0;
  const finished = timed && elapsed >= length;
  useEffect(() => {
    if (paused || !timed || finished) return;
    const id = setInterval(() => setElapsed((ms) => (pausedNow.current ? ms : Math.min(ms + TICK_MS, length))), TICK_MS);
    return () => clearInterval(id);
  }, [paused, timed, finished, index, length]);

  const progress = !card ? 0 : card.waitsForTap ? 1 : Math.min(elapsed / length, 1);
  return {
    index,
    paused,
    progress,
    /** The card has played through and is waiting for the viewer. */
    done: finished && index < last,
    next,
    prev,
    goTo,
    pause: useCallback(() => setPaused(true), [setPaused]),
    resume: useCallback(() => setPaused(false), [setPaused]),
    toggle: useCallback(() => setPaused((was) => !was), [setPaused]),
  };
}
